<template>
  <div class="panel-body">
    <div class="panel-body_head">
      <b-row v-if="showControls" class="d-flex justify-content-end mt-1">
        <b-col cols="8">
          <b-form inline class="mt-1">
            <label v-if="showSortZ" class="mr-1">z-Axis</label>
            <b-form-select
              v-if="showSortZ"
              v-model="sortZ"
              class="mr-1"
              :options="sortZOptions"
              size="sm"
            ></b-form-select>

            <label v-if="showColor" class="mr-1">Color</label>
            <b-form-select
              v-if="showColor"
              v-model="color"
              class="mr-1"
              :options="colorOptionsSorted"
              size="sm"
            ></b-form-select>

            <label v-if="showSortX" class="mr-1">Sort x-Axis</label>
            <b-form-select
              v-if="showSortX"
              v-model="sortX"
              class="mr-1"
              :options="sortXOptions"
              size="sm"
            ></b-form-select>
            <b-form-select
              v-if="showSortX && sortX !== 'clustering_docs'"
              v-model="sortXDirection"
              class="mr-1"
              :options="directionOptions"
              size="sm"
            ></b-form-select>

            <label v-if="showSortY" class="mr-1">Sort y-Axis</label>
            <b-form-select
              v-if="showSortY"
              v-model="sortY"
              class="mr-1"
              :options="sortYOptions"
              size="sm"
            ></b-form-select>
            <b-form-select
              v-if="showSortY && sortY !== 'clustering_docs'"
              v-model="sortYDirection"
              class="mr-1"
              :options="directionOptions"
              size="sm"
            ></b-form-select>

            <label v-if="showMetric" class="mr-1">Metric</label>
            <b-form-select
              v-if="showMetric"
              v-model="metric"
              class="mr-1"
              :options="colorOptionsSorted"
              size="sm"
            ></b-form-select>

            <label v-if="showMetricX" class="mr-1">Metric-X</label>
            <b-form-select
              v-if="showMetricX"
              v-model="metricX"
              class="mr-1"
              :options="metricXOptions"
              size="sm"
            ></b-form-select>

            <label v-if="showMetricY" class="mr-1">Metric-Y</label>
            <b-form-select
              v-if="showMetricY"
              v-model="metricY"
              class="mr-1"
              :options="metricYOptions"
              size="sm"
            ></b-form-select>

            <b-form-group v-if="showTypeCats">
              <b-form-checkbox v-model="typeCats" size="sm" switch
                >Show types &amp; categories</b-form-checkbox
              >
            </b-form-group>
            <b-form-group v-if="showNormalize">
              <b-form-checkbox
                v-model="normalizeOverX"
                size="sm"
                class="mt-2"
                switch
                >Normalize over X</b-form-checkbox
              >
            </b-form-group>
          </b-form>
        </b-col>
        <b-col v-if="showRecency" cols="2">
          <b-form-group
            id="input-group-recency"
            label-for="input-recency"
            class="year-slider"
          >
            <Slider
              v-if="recencyRange"
              v-model="recencyLimit"
              :decimals="2"
              :min="recencyRange[0]"
              :max="recencyRange[1]"
              :type="'recency'"
            ></Slider>
          </b-form-group>
        </b-col>
        <b-col v-if="showSize" cols="2">
          <b-form-group
            id="input-group-size"
            label-for="input-size"
            class="year-slider"
          >
            <Slider
              v-if="sizeRange"
              v-model="sizeLimit"
              :min="sizeRange[0]"
              :max="sizeRange[1]"
              :type="'size'"
            ></Slider>
          </b-form-group>
        </b-col>
      </b-row>
    </div>

    <div class="panel-body_content">
      <h2 class="my-3">Edit Analysis</h2>

      <b-row class="mt-2">
        <b-col cols="4" class="mt-2">
          <b-row>
            <b-col>
              <b-form-group>
                <b-form-checkbox v-model="form.addClustering" size="sm" switch
                  >Clustering</b-form-checkbox
                >
              </b-form-group>
            </b-col>
            <b-col>
              <b-form-group>
                <b-form-checkbox v-model="form.includeSubset" size="sm" switch
                  >Subset</b-form-checkbox
                >
              </b-form-group>
            </b-col>
          </b-row>
        </b-col>
        <b-col cols="8" class="pl-4 pr-4">
          <b-form-group id="input-group-range" class="year-slider">
            <Slider
              v-if="yearRange"
              v-model="yearLimit"
              :min="yearRange[0]"
              :max="yearRange[1]"
            ></Slider>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row class="mt-2">
        <b-col v-for="layout in layoutTypes" :key="layout.value">
          <div
            class="panel-body_chart-type medium"
            :class="{ active: layoutSelected === layout.value }"
            @click="selectType(layout.value)"
          >
            <img :src="'/images/charts/' + layout.icon" />
            <span>{{ layout.text }}</span>
          </div>
        </b-col>
      </b-row>

      <b-row class="mt-3">
        <b-col>
          <label size="sm">{{ xLabel() }}</label>
          <ExploreAnalysisCreateAxis
            :axis="axis.x"
            :multiple="false"
          ></ExploreAnalysisCreateAxis>
        </b-col>
        <b-col v-if="['heatmap','clustermap'].includes(layoutSelected)">
          <label size="sm">Y-Axis</label>
          <ExploreAnalysisCreateAxis
            :axis="axis.y"
            :multiple="false"
          ></ExploreAnalysisCreateAxis>
        </b-col>
        <b-col v-if="form.includeSubset">
          <label size="sm">Z-Axis</label>
          <ExploreAnalysisCreateAxis
            :axis="axis.z"
            :multiple="false"
          ></ExploreAnalysisCreateAxis>
        </b-col>
      </b-row>
      <b-row class="mb-2">
        <b-col class="d-flex justify-content-end">
          <b-btn size="sm" variant="primary" class="mr-2 ml-2" @click="update()"
            ><i class="fa fa-check"></i> Update Analysis</b-btn
          >
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import _ from "lodash";
import { mapActions, mapGetters, mapState } from "vuex";
import AnalysisHelpers from "@/components/Explore/ExploreAnalysis/AnalysisHelpers";
import { EventBus } from "@/eventBus";
import ExploreAnalysisCreateAxis from "../ExploreAnalysisCreateAxis";
import Slider from "../Slider";

export default {
  name: "AnalysisSettings",
  components: {
    ExploreAnalysisCreateAxis,
    Slider,
  },
  props: {
    settings: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      analysis: null,
      yearRange: null,
      yearLimit: [],
      years: [],
      form: {
        type: null,
        name: null,
        description: null,
        recentyear: null,
        startyear: null,
        stopyear: null,
        includeSubset: false,
        addClustering: false,
      },
      axis: {
        x: {},
        y: {},
        z: {},
      },
      layoutTypes: [
        { value: "worldmap", text: "World Map", icon: "global.svg" },
        { value: "heatmap", text: "Heat Map", icon: "pixels.png" },
        { value: "clustermap", text: "Cluster Map", icon: "clustermap.svg" },
        { value: "onedheatmap", text: "1D Heat Map", icon: "grid.svg" },
        { value: "scatterplot", text: "Scatter Plot", icon: "scatter.svg" },
        { value: "barchart", text: "Bar Chart", icon: "col-chart.svg" },
        { value: "columnchart", text: "Column Chart", icon: "stats.svg" },
      ],
      view: null,
      layoutSelected: null,
      color: "size",
      metric: "size",
      colorOptions: [
        { value: "size", text: "Size" },
        { value: "recency", text: "Recency" },
      ],
      directionOptions: [
        { value: "asc", text: "Ascending" },
        { value: "desc", text: "Descending" },
      ],
      sortX: "normal",
      sortXDirection: "asc",
      sortY: "normal",
      sortYDirection: "asc",
      sortZ: 0,
      sortZOptions: [{ value: 0, text: "All" }],
      metricX: "recency",
      metricXOptions: [
        { value: "size", text: "Size" },
        { value: "recency", text: "Recency" },
      ],
      metricY: "size",
      metricYOptions: [
        { value: "size", text: "Size" },
        { value: "recency", text: "Recency" },
      ],
      typeCats: false,
      normalizeOverX: false,
      sizeLimit: [0, 10],
      sizeRange: null,
      recencyLimit: [0, 10],
      recencyRange: null,
      showTitle: true,
      showControls: true,
      showQuery: false,
      showColor: false,
      showSortZ: false,
      showSortX: false,
      showSortY: false,
      showNormalize: false,
      showRecency: false,
      showSize: false,
      showMetric: false,
      showMetricX: false,
      showMetricY: false,
      showTypeCats: false,
    };
  },
  computed: {
    ...mapGetters(["analyseById"]),
    ...mapState({
      analyses: (state) => state.analyse.analyses,
      documentsPerYear: (state) => state.document.documentsPerYear,
      indices: (state) => state.ttc.indices,
      subsetFilter: (state) => state.filter.subsetFilter,
    }),
    ringStringified: {
      get() {
        return JSON.stringify(this.ring);
      },
      set(val) {
        this.query = JSON.parse(val);
      },
    },
    layoutOptions() {
      if (this.metas["Country"]) {
        return this.layoutTypes;
      }
      return this.layoutTypes.filter((t) => t.value !== "worldmap");
    },
    colorOptionsSorted() {
      const generalOptions = [];
      const filterOptions = [];

      this.colorOptions.forEach((o) => {
        if (o.text.includes("filter")) {
          filterOptions.push(o);
        } else {
          generalOptions.push(o);
        }
      });

      if (filterOptions.length > 0) {
        return [
          { text: "General", value: null, disabled: true },
          ...generalOptions,
          { text: "Filter", value: null, disabled: true },
          ...filterOptions,
        ];
      }

      return generalOptions;
    },
    sortXOptions() {
      return [
        { value: "normal", text: "Normal" },
        { value: "size", text: "Size" },
        { value: "recency", text: "Recency" },
        {
          value: "clustering",
          text: "Clustering",
          disabled: !this.form.addClustering,
        },
        {
          value: "clustering_docs",
          text: "Clustering (Documents)",
          disabled: !this.form.addClustering,
        },
      ];
    },
    sortYOptions() {
      return [
        { value: "normal", text: "Normal" },
        { value: "size", text: "Size" },
        { value: "size_des", text: "Size descending" },
        { value: "recency", text: "Recency" },
        { value: "recency_des", text: "Recency descending" },
        {
          value: "clustering",
          text: "Clustering",
          disabled: !this.form.addClustering,
        },
        {
          value: "clustering_docs",
          text: "Clustering (Documents)",
          disabled: !this.addClustering,
        },
      ];
    },
  },
  watch: {
    yearLimit(value) {
      if (this.form.startyear !== value[0]) {
        this.form.startyear = value[0];
      }
      if (this.form.recentyear !== value[1]) {
        this.form.recentyear = value[1];
      }
      if (this.form.stopyear !== value[2]) {
        this.form.stopyear = value[2];
      }
    },
    color() {
      this.updateSettings();
    },
    sortX() {
      this.updateSettings();
    },
    sortXDirection() {
      this.updateSettings();
    },
    sortY() {
      this.updateSettings();
    },
    sortYDirection() {
      this.updateSettings();
    },
    sortZ() {
      this.updateSettings();
    },
    normalizeOverX() {
      this.updateSettings();
    },
    sizeLimit() {
      this.updateSettings();
    },
    recencyLimit() {
      this.updateSettings();
    },
    metric() {
      this.updateSettings();
    },
    metricX() {
      this.updateSettings();
    },
    metricY() {
      this.updateSettings();
    },
    typeCats() {
      this.updateSettings();
    },
    settings(value) {
      this.view = value.view;
      this.color = value.color;
      this.colorOptions = value.colorOptions;
      this.sortZ = value.sortZ;
      this.sortZOptions = value.sortZOptions;
      this.sizeLimit = value.sizeLimit;
      this.recencyLimit = value.recencyLimit;
      this.sizeRange = value.sizeRange;
      this.recencyRange = value.recencyRange;
      this.metricX = value.metricX;
      this.metricXOptions = value.metricXOptions;
      this.metricY = value.metricY;
      this.metricYOptions = value.metricYOptions;
      this.typeCats = value.typeCats;
      this.form.addClustering = value.clustering;
      this.initControls(value);
    },
  },
  mounted() {
    if (this.analyses.length > 0) {
      this.extractAnalyseConfig();
    } else {
      this.loadAnalyses().then(() => {
        this.extractAnalyseConfig();
      });
    }
    this.initYears();
  },
  methods: {
    ...mapActions([
      "saveSubsetFilter",
      "loadAnalyses",
      "loadFilters",
      "updateAnalyse",
    ]),
    initAxis() {
      ["x", "y", "z"].forEach((d) => {
        this.axis[d] = {
          dimension: d,
          levels: [
            {
              id: 1,
              modifiers: [],
              grouped: false,
              includeAll: false,
              linked: false,
              layers: [],
              //max: 1000
            },
          ],
        };
      });
    },
    initYears() {
      if (this.documentsPerYear.length > 0) {
        const yearMin = parseInt(_.minBy(this.documentsPerYear, "year").year);
        const yearMax = parseInt(_.maxBy(this.documentsPerYear, "year").year);
        this.yearRange = [yearMin, yearMax];
        this.yearLimit = [
          this.form.startyear,
          this.form.recentyear,
          this.form.stopyear,
        ];
        this.years = this.yearLimit;
      }
    },
    initControls(settings) {
      this.showQuery = settings.controls.showQuery;
      this.showColor = settings.controls.showColor;
      this.showSortZ = settings.controls.showSortZ;
      this.showSortX = settings.controls.showSortX;
      this.showSortY = settings.controls.showSortY;
      this.showNormalize = settings.controls.showNormalize;
      this.showRecency = settings.controls.showRecency;
      this.showSize = settings.controls.showSize;
      this.showMetric = settings.controls.showMetric;
      this.showMetricX = settings.controls.showMetricX;
      this.showMetricY = settings.controls.showMetricY;
      this.showTypeCats = settings.controls.showTypeCats;
    },
    buildQueryLayers(axis) {
      const preselect = { };
      return AnalysisHelpers.buildQueryLayers(axis, preselect);
    },
    update() {
      let query;
      if (!_.isEmpty(this.axis.x)) {
        if (this.axis.x.levels.length === 0) {
          this.$toasted.show("Please Define Level(s)", { type: "error" });
        } else {
          query = {
            xlayers: this.buildQueryLayers(this.axis.x),
            ylayers:
              ["heatmap","clustermap"].includes(this.layoutSelected)
                ? this.buildQueryLayers(this.axis.y)
                : [],
            zlayers: this.form.includeSubset
              ? this.buildQueryLayers(this.axis.z)
              : [],
            subset_filters:
              this.subsetFilter.length > 0 ? [this.subsetFilter] : [],
            startyear: this.form.startyear,
            recentyear: this.form.recentyear,
            stopyear: this.form.stopyear,
            postprocessing: this.form.addClustering ? ["clustering"] : [],
          };
        }
      }

      const payload = {
        _id: this.analysis._id,
        name: this.form.name,
        description: this.form.description,
        config: {
          type: this.layoutSelected,
          query: query,
        },
        // Add some extra data to easily load when editing analysis
        axis: this.axis,
        includeSubset: this.form.includeSubset,
        addClustering: this.form.addClustering,
      };
      //console.log(payload);
      this.updateAnalyse({
        analyseId: this.analysis._id,
        analyse: payload,
      }).then(() => {
        this.$toasted.show("Analysis updated");
        setTimeout(() => {
          this.$emit("redrawAnalysis");
        }, 1000);
      });
      EventBus.$emit("toggleTopPanel", null);
    },
    selectType(type) {
      this.layoutSelected = type;
    },
    updateSettings() {
      let sortX = this.sortX;
      if (
        sortX !== "clustering" &&
        sortX !== "clustering_docs" &&
        this.sortXDirection === "desc"
      ) {
        sortX += "_des";
      }

      let sortY = this.sortY;
      if (
        sortY !== "clustering" &&
        sortY !== "clustering_docs" &&
        this.sortYDirection === "desc"
      ) {
        sortY += "_des";
      }

      this.$emit("updateSettings", {
        view: this.view,
        color: this.color,
        metric: this.metric,
        sortX: sortX,
        sortY: sortY,
        sortZ: this.sortZ,
        normalizeOverX: this.normalizeOverX,
        sizeLimit: this.sizeLimit,
        recencyLimit: this.recencyLimit,
        sizeRange: this.sizeRange,
        recencyRange: this.recencyRange,
        metricX: this.metricX,
        metricY: this.metricY,
        typeCats: this.typeCats,
      });
    },
    extractAnalyseConfig() {
      this.analysis = this.analyseById({ id: this.$route.params.id });
      this.form.name = this.analysis.name;
      this.form.description = this.analysis.description;
      this.form.type = this.analysis.config.type;
      this.form.recentyear = this.analysis.config.query.recentyear;
      this.form.startyear = this.analysis.config.query.startyear;
      this.form.stopyear = this.analysis.config.query.stopyear;
      this.form.includeSubset = this.analysis.includeSubset;
      this.form.addClustering = this.analysis.addClustering;

      this.yearLimit = [
        this.form.startyear,
        this.form.recentyear,
        this.form.stopyear,
      ];
      this.years = this.yearLimit;

      this.layoutSelected = this.analysis.config.type;

      this.axis.x = this.analysis.axis.x;
      this.axis.y = this.analysis.axis.y;
      this.axis.z = this.analysis.axis.z;

      this.axisReady = true;
    },
    xLabel(){
      if(['columnchart','heatmap','linechart','clustermap'].includes(this.layoutSelected)){
        return 'X-Axis'
      }
      else if(this.layoutSelected === 'barchart'){
        return 'Y-Axis'
      }
      else{
        return 'Axis'
      }
    }
  },
};
</script>
